import { zodResolver } from '@hookform/resolvers/zod';
import { PasswordResetRequestSchema } from '@utazzo/shared';
import { UzButton, UzForm, UzFormField, UzInput } from '@utazzo/ui';
import { ChevronLeft, Mail } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useForgotPassword } from '../use-auth';

import { AuthLayout } from './AuthLayout';

import type { z } from 'zod';

type FormValues = z.infer<typeof PasswordResetRequestSchema>;

export const ForgotPasswordPage = (): React.JSX.Element => {
  const { t } = useTranslation();
  const forgot = useForgotPassword();

  const form = useForm<FormValues>({
    resolver: zodResolver(PasswordResetRequestSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = form.handleSubmit((values) => {
    forgot.mutate(values.email, { onSuccess: () => form.reset() });
  });

  return (
    <AuthLayout
      title={t('auth.forgot.title')}
      subtitle={t('auth.forgot.subtitle')}
      topSlot={
        <Link
          to="/login"
          className="text-muted-foreground hover:text-foreground mb-1 -ml-1 inline-flex items-center gap-1 self-start text-sm transition-colors"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
          {t('common.backToLogin')}
        </Link>
      }
      footer={
        <Link to="/login" className="text-primary font-medium hover:underline">
          {t('common.backToLogin')}
        </Link>
      }
    >
      <UzForm form={form} onSubmit={onSubmit}>
        <UzFormField name="email">
          {({ field, error }) => (
            <UzInput
              {...field}
              type="email"
              autoComplete="email"
              label={t('auth.forgot.emailLabel')}
              leftAdornment={<Mail className="h-4 w-4" aria-hidden />}
              error={error}
              required
              autoFocus
              disabled={forgot.isPending}
            />
          )}
        </UzFormField>

        <UzButton type="submit" fullWidth loading={forgot.isPending} className="mt-2">
          {forgot.isPending ? t('auth.forgot.submitting') : t('auth.forgot.submit')}
        </UzButton>
      </UzForm>
    </AuthLayout>
  );
};
